export const STEPPER_SIZES = ['sm', 'lg'] as const;
export type SStepperSize = (typeof STEPPER_SIZES)[number];

export type SStepperItem = {
  label: string;
  value?: string;
  group?: string;
  completed?: boolean;
  error?: boolean;
};

const V = (path: string) => `var(--cmp-stepper-${path})`;
const VF = (path: string, fallback: string) => `var(--cmp-stepper-${path}, ${fallback})`;

export const STEPPER_SIZE_CONFIG: Record<
  SStepperSize,
  {
    sequenceSize: string;
    fontSize: string;
    lineHeight: string;
    fontWeightActive: number;
    fontWeightDefault: number;
  }
> = {
  sm: {
    sequenceSize: V('sm-sequence-size'),
    fontSize: '12px',
    lineHeight: '20px',
    fontWeightActive: 700,
    fontWeightDefault: 500,
  },
  lg: {
    sequenceSize: V('lg-sequence-size'),
    fontSize: '14px',
    lineHeight: '24px',
    fontWeightActive: 700,
    fontWeightDefault: 500,
  },
};

export const STEPPER_LAYOUT = {
  containerGap: V('horizontal-gap'),
  itemGap: V('item-gap'),
  dashWidth: V('horizontal-dash-width'),
  sequenceRadius: V('sequence-radius'),
  sequenceBorderWidth: V('sequence-border-width'),
  sequenceIcon: V('sequence-icon'),
};

export const STEPPER_COLORS = {
  dash: V('horizontal-dash-color'),
  sequenceBorder: {
    active: V('sequence-border-active'),
    default: V('sequence-border-default'),
    completed: VF('sequence-border-completed', '#93C4FF'),
  },
  sequenceBg: {
    active: V('sequence-bg-active'),
    default: V('sequence-bg-default'),
    completed: VF('sequence-bg-completed', '#EFF6FF'),
  },
  sequenceText: {
    active: V('sequence-text-active'),
    default: V('sequence-text-default'),
    completed: VF('sequence-text-completed', '#0075FF'),
  },
  text: {
    active: V('text-active'),
    default: V('text-default'),
    completed: V('text-completed'),
  },
};

export const STEPPER_HORIZONTAL_COMPLETED_COLORS = {
  sequenceBorder: STEPPER_COLORS.sequenceBorder.completed,
  sequenceBg: STEPPER_COLORS.sequenceBg.completed,
  sequenceIcon: STEPPER_COLORS.sequenceText.completed,
  text: STEPPER_COLORS.text.completed,
};

export const STEPPER_VERTICAL_LAYOUT = {
  width: VF('vertical-width', '240px'),
  itemGap: VF('vertical-item-gap', '12px'),
  itemPaddingX: VF('vertical-item-paddingX', '16px'),
  itemPaddingY: VF('vertical-item-paddingY', '8px'),
  titlePaddingTop: VF('vertical-title-paddingTop', '12px'),
  titlePaddingBottom: VF('vertical-title-paddingBottom', '4px'),
  titleFontSize: VF('vertical-title-font-size', '11px'),
  titleLineHeight: VF('vertical-title-line-height', '18px'),
  accentStripeWidth: VF('vertical-item-accentStripe-width', '2px'),
  accentStripeColor: VF('vertical-item-accentStripe-color', '#64ABFF'),
};

export const STEPPER_VERTICAL_COLORS = {
  itemBg: {
    default: VF('vertical-item-bg-default', '#FFFFFF'),
    hover: VF('vertical-item-bg-hover', '#F6F6F6'),
    selected: VF('vertical-item-bg-selected', '#EFF6FF'),
  },
  titleText: VF('vertical-title-text', '#004290'),
  textDefault: STEPPER_COLORS.text.default,
  textCompleted: STEPPER_COLORS.text.completed,
  sequenceBorderActive: STEPPER_COLORS.sequenceBorder.active,
  sequenceBgActive: STEPPER_COLORS.sequenceBg.active,
  sequenceTextActive: STEPPER_COLORS.sequenceText.active,
  sequenceTextDefault: STEPPER_COLORS.sequenceText.default,
  sequenceBorderCompleted: VF('vertical-sequence-border-completed', '#93C4FF'),
  sequenceBgCompleted: VF('vertical-sequence-bg-completed', '#EFF6FF'),
  sequenceIconCompleted: VF('vertical-sequence-icon-completed', '#0075FF'),
  errorBadge: '#FB4444',
};
